import React, { useCallback, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty } from "lodash";
import uniqid from "uniqid";
import moment from "moment";

import { actions as commUpgradeActions } from "features/Community/Upgrades/_ducks";
import { selectCapitalCosts, selectCommUpgradePackages, selectUpgradePackagesIds } from "./_ducks/selectors";
import { selectCommunityObject } from "store/community/selectors";
import { selectUserUid } from "store/users/selectors";
import { addUpgradeToCommunity, updateCommunitySettings } from "store/community/actions";
import { getUpgradesToAdd } from "utils/communities";
import { useStateSelector } from "utils/field";

import { getTableUpgradesMissingFromEcmInputMap, getMissingEcmInputMapCodes } from "utils/upgrades/ecmInputMap";

import TabHeader from "components/TabHeader";
import InfoDropdown from "components/InfoDropdown";
import ToggleVisibility from "components/ToggleVisibility/ToggleVisibility";
import Checkbox from "components/Input/Checkbox";
import Button from "components/Button";
import CommunitiesUpgradesTable from "./UpgradesTable";
import PackageDeleteDialog from "features/Model/Upgrades/PackageDeleteDialog/PackageDeleteDialog";
import AddPackageDialog from "./UpgradesTable/AddPackageDialog";
import CodeWarning from "features/Model/Upgrades/UpgradesTable/CodeWarning";

import classes from "./style.module.scss";

import AddIcon from "assets/images/icons/JSX/Add";

const { addCommUpgradePackage, adjustCommPackageCost } = commUpgradeActions;

const Upgrades = ({
    change,
    uid,
    communityObj,
    capitalCosts,
    upgradePackageIds,
    upgradePackages,
    addUpgradeToCommunity,
    adjustCommPackageCost,
    addCommUpgradePackage,
    updateCommunitySettings,
}) => {
    const tableRef = useRef();

    const accessor = `community.community.communityDetails.uiSettings`;

    //upgrades
    const {
        ecmInputMap = {},
        modelDir: { models = {} },
    } = communityObj || {};

    const [dialogOpen, toggleDialog] = useState(false);
    const [selectedPackage, toggleSelectedPackage] = useState("");
    const [toDelete, toggleToDelete] = useState(false);

    const { upgrades: { hideArchived = false, hideRows = false, showCost = false } = {} } =
        useStateSelector(`${accessor}`) || {};

    const simulateDisabled = false;

    const toggleArchiveHide = () => {
        updateCommunitySettings({ upgrades: { hideArchived: !hideArchived } });
        return change(`${accessor}.upgrades.hideArchived`, !hideArchived);
    };
    const toggleRowHide = () => {
        updateCommunitySettings({ upgrades: { hideRows: !hideRows } });
        return change(`${accessor}.upgrades.hideRows`, !hideRows);
    };
    const toggleShowCost = () => {
        updateCommunitySettings({ upgrades: { showCost: !showCost } });
        return change(`${accessor}.upgrades.showCost`, !showCost);
    };

    const duplicateUpgrade = useCallback(
        async (packageId, name) => {
            const numberOfPackages = upgradePackageIds.length;

            const dupPackageId = uniqid("package-");
            const dateObj = {
                created: moment().format("LLLL"),
                lastEdited: moment().format("LLLL"),
            };
            const uiObj = { ui: { displayOrder: numberOfPackages } };
            const initUpgrades = upgradePackages?.[packageId]?.upgrades || {};

            // Give unique keys for duplicated upgradeIds
            const upgrades = Object.keys(initUpgrades).reduce((acc, curr) => {
                const newKey = uniqid("upgrade-");
                return {
                    ...acc,
                    [newKey]: initUpgrades[curr] || {},
                };
            }, {});

            // Copy original package's costs to initialize duplicated package's cost for each model
            Object.keys(models).forEach((modelId) => {
                adjustCommPackageCost({
                    modelId,
                    packageId: dupPackageId,
                    amount: capitalCosts?.[modelId]?.[packageId] ?? 0,
                });
            });

            // Add duplicated Upgrade Package to commUpgrades state (Populates upgrades table)
            addCommUpgradePackage({
                packageId: dupPackageId,
                packageUpdates: {
                    name: `${name} (copy)`,
                    ...dateObj,
                    ...uiObj,
                    upgrades,
                },
            });
            // Mirror AddPackageDialog: one package upgrade can map to several ecmInputMap rows (e.g. DHW + DWHR).
            const upgradesToAdd = getUpgradesToAdd(upgrades, ecmInputMap);
            for (const upgrade of upgradesToAdd) {
                const upgradeType = Object.values(upgrade)[0].packageValues.upgradeSubType;
                const upgradeKey = Object.keys(upgrade)[0];
                const upgradeWithPackageId = {
                    [upgradeKey]: {
                        ...upgrade[upgradeKey],
                        packageValues: {
                            ...upgrade[upgradeKey].packageValues,
                            upgradePackageId: dupPackageId,
                        },
                    },
                };
                addUpgradeToCommunity(upgradeType, upgradeWithPackageId);
            }

            tableRef.current.scrollLeft = tableRef.current.scrollWidth;

            toggleSelectedPackage("");
        },
        [selectedPackage, upgradePackageIds, upgradePackages]
    );

    // Get missing model codes for the upgrade packages
    const missingCodes = useMemo(
        () => getMissingEcmInputMapCodes(upgradePackages, ecmInputMap),
        [upgradePackages, ecmInputMap]
    );

    const missingCodesLabels = useMemo(
        () =>
            Object.entries(
                missingCodes.reduce((acc, [currCodeRef, { codeLabel }]) => ({ ...acc, [currCodeRef]: codeLabel }), {})
            ),
        [missingCodes]
    );

    const ecmTableSyncIssues = useMemo(
        () => getTableUpgradesMissingFromEcmInputMap(upgradePackages || {}, ecmInputMap),
        [upgradePackages, ecmInputMap]
    );

    const ecmSyncIssueLine = (issue) => {
        const base = `${issue.packageName}: ${issue.category} - ${issue.labelText}`;

        if (issue.reason === "package_id_mismatch") {
            return `${base} — package ID mismatch; re-apply, edit, or remove.`;
        }
        if (issue.reason === "missing_category") {
            return `${base} — measure category is missing; edit or remove.`;
        }
        if (issue.reason === "missing_ecm_row") {
            return `${base} — row not saved in measure map; re-apply, edit, or remove.`;
        }
        return `${base} — re-apply, edit, or remove.`;
    };

    return (
        <>
            <TabHeader
                className={classes.header}
                title="Upgrades"
                description="Define upgrade packages to investigate multiple improvement pathways."
                additionalContent={
                    <div className={classes.additionalContent}>
                        {!isEmpty(missingCodesLabels) && <CodeWarning missingCodesLabels={missingCodesLabels} />}
                        {ecmTableSyncIssues.length > 0 && (
                            <InfoDropdown
                                isWarning
                                label={"Upgrades missing critical data"}
                                className={classes.codeWarningContainer}
                                contentClassName={classes.codeWarningContent}
                            >
                                <div className={classes.codeWarningListItem}>
                                    <p>
                                        Please re-apply, edit, or remove these table rows, or edit the upgrade in the
                                        Upgrade Library:
                                    </p>
                                </div>
                                {ecmTableSyncIssues.map((issue, idx) => (
                                    <div
                                        key={`ecm-sync-${issue.packageId}-${issue.upgradeId}-${idx}`}
                                        className={classes.codeWarningListItem}
                                    >
                                        <p>{ecmSyncIssueLine(issue)}</p>
                                    </div>
                                ))}
                            </InfoDropdown>
                        )}
                        <ToggleVisibility
                            className={classes.rowVisibilityToggle}
                            onClick={toggleArchiveHide}
                            isHide={hideArchived}
                            label={hideArchived ? "Show Archived" : "Hide Archived"}
                        />
                        <ToggleVisibility
                            className={classes.rowVisibilityToggle}
                            onClick={toggleRowHide}
                            isHide={hideRows}
                            label={hideRows ? "Show Rows" : "Hide Rows"}
                        />
                        <Checkbox
                            label="Show Costs"
                            input={{
                                onChange: () => toggleShowCost((prev) => !prev),
                                value: showCost,
                            }}
                            className={classes.showCostsCheckbox}
                        />
                        {/* <Button
                            icon={DownloadIcon}
                            className={classes.exportPdfButton}
                            disabled={simulateDisabled}
                            type={"lightGrey"}
                            onClick={() => toggelExportPFDDrawerOpen(!exportPFDDrawerOpen)}
                        >
                            Export PDF
                        </Button> */}
                        <Button
                            icon={AddIcon}
                            className={classes.addButton}
                            disabled={simulateDisabled || isEmpty(models)}
                            onClick={() => {
                                toggleDialog(true);
                                toggleSelectedPackage("");
                            }}
                        >
                            Add New Package
                        </Button>
                    </div>
                }
            />
            <CommunitiesUpgradesTable
                ref={tableRef}
                accessor={accessor}
                change={change}
                uid={uid}
                hideArchived={hideArchived}
                showCost={showCost}
                toggleShowCost={toggleShowCost}
                toggleToDelete={toggleToDelete}
                duplicateUpgrade={duplicateUpgrade}
                ecmInputMap={ecmInputMap}
                missingCodes={missingCodes}
            />
            <AddPackageDialog
                isOpen={dialogOpen}
                toggleOpen={toggleDialog}
                upgradePackageIds={upgradePackageIds}
                selected={selectedPackage}
                selectedName={upgradePackages[selectedPackage]?.name || ""}
                toggleSelected={toggleSelectedPackage}
                numberOfPackages={upgradePackageIds.length}
                tableRef={tableRef}
            />
            <PackageDeleteDialog toDelete={toDelete} toggleToDelete={toggleToDelete} isCommunity={true} />
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    uid: selectUserUid,
    communityObj: selectCommunityObject,
    capitalCosts: selectCapitalCosts,
    upgradePackageIds: selectUpgradePackagesIds,
    upgradePackages: selectCommUpgradePackages,
});

const mapDispatchToProps = (dispatch) => ({
    addUpgradeToCommunity: (sectionType, upgrade) => dispatch(addUpgradeToCommunity(sectionType, upgrade)),
    adjustCommPackageCost: ({ modelId, packageId, amount }) => {
        dispatch(adjustCommPackageCost({ modelId, packageId, amount }));
    },
    addCommUpgradePackage: ({ packageId, packageUpdates }) => {
        dispatch(addCommUpgradePackage({ packageId, packageUpdates }));
    },
    updateCommunitySettings: ({ upgrades }) => dispatch(updateCommunitySettings({ upgrades })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Upgrades);
